
import React from 'react';
import { Check, ArrowRightFromLine, RefreshCw, Trash2, Sparkles, X } from 'lucide-react';
import { cn } from '../lib/utils';

interface RefineMenuProps {
    result: string;
    isLoading: boolean;
    onAction: (action: 'replace' | 'addBelow' | 'discard' | 'regenerate') => void;
    onClose: () => void;
    position?: { x: number; y: number }; // Made optional, as we default to center
}

export const RefineMenu: React.FC<RefineMenuProps> = ({ result, isLoading, onAction, onClose, position }) => {
    return (
        <div
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[2000] w-80 bg-white border border-slate-200 rounded-xl shadow-2xl animate-in fade-in zoom-in-95 duration-200 overflow-hidden text-left"
        // We ignore custom position for now to ensure it's always visible/centered (responsive behavior requested)
        >
            <div className="px-3 py-2.5 bg-blue-50/50 border-b border-blue-100 flex items-center justify-between">
                <span className="text-xs font-bold text-blue-700 uppercase tracking-wider flex items-center gap-2">
                    <Sparkles size={12} />
                    Refined Text
                </span>
                <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
                    <X size={14} />
                </button>
            </div>

            {isLoading ? (
                <div className="p-8 flex flex-col items-center justify-center gap-3 text-slate-400">
                    <RefreshCw size={24} className="animate-spin text-blue-500" />
                    <span className="text-xs font-medium">Refining text...</span>
                </div>
            ) : (
                <div className="flex flex-col">
                    <div className="p-4 bg-slate-50/50 max-h-[300px] overflow-y-auto text-sm text-slate-700 leading-relaxed border-b border-slate-100">
                        {result}
                    </div>
                    <div className="p-1.5 flex flex-col gap-0.5">
                        <button
                            onClick={() => onAction('replace')}
                            className="flex items-center gap-2 px-2 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded-lg transition-colors text-left"
                        >
                            <Check size={14} className="text-slate-400" />
                            <span>Replace selection</span>
                        </button>
                        <button
                            onClick={() => onAction('addBelow')}
                            className="flex items-center gap-2 px-2 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded-lg transition-colors text-left"
                        >
                            <ArrowRightFromLine size={14} className="text-slate-400 rotate-90" />
                            <span>Insert below</span>
                        </button>
                        <button
                            onClick={() => onAction('regenerate')}
                            className="flex items-center gap-2 px-2 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors text-left"
                        >
                            <RefreshCw size={14} />
                            <span>Try again</span>
                        </button>
                        <div className="h-[1px] bg-slate-100 my-1" />
                        <button
                            onClick={() => onAction('discard')}
                            className="flex items-center gap-2 px-2 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors text-left"
                        >
                            <Trash2 size={14} />
                            <span>Discard</span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
